import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils'
import {useDataTable} from '@/hooks/useDataTable-hook'
import {GetFormattedProductData} from '@/models/ProductListModel'
import {ProductListColConfig} from '@/services/MockData/Products/ProductListColConfig'
import {getProductCsvFile, getProductList} from '@/services/ProductsService/product'
import {useQueries} from '@tanstack/react-query'
import {useMemo, useState} from 'react'
import {useAppSelector} from '@/store'

import { TAG_COLOR } from '@/utils/constants/colors'
export const useProductList = (options = {}) => {
  // Extract productIds from salesTarget state
  const [totalRecords, setTotalRecords] = useState(0)
  const salesTargetData = useAppSelector(state => state.salesTarget)
  const Enum_PRODUCT_ID_KEYS = {
    INCLUDED_PRODUCT_IDS: 'includedProductIds',
    EXCLUDED_PRODUCT_IDS: 'excludedProductIds'
  }
  const getIdsFromRuleSet = (ruleSet, key)=>{
    return  ruleSet?.rules?.[0]?.[key] || []
  }
  const {isReviewPage = false} = options
  const extractProductIds = () => {
    if (!salesTargetData) {
      return []
    }
    let includedIds = []
    let excludedIds = []
    if (isReviewPage) {
      if (!salesTargetData) return [] 
      if (!salesTargetData?.ruleSet?.rules?.[0]) {
        return []
      }
      if (salesTargetData.tabWiseData?.activeTab === 'child') {
        includedIds = getIdsFromRuleSet(salesTargetData.tabWiseData?.child?.ruleSet, Enum_PRODUCT_ID_KEYS.INCLUDED_PRODUCT_IDS)
        excludedIds = getIdsFromRuleSet(salesTargetData.tabWiseData?.child?.ruleSet, Enum_PRODUCT_ID_KEYS.EXCLUDED_PRODUCT_IDS)
      } else {
        includedIds = getIdsFromRuleSet(salesTargetData.ruleSet, Enum_PRODUCT_ID_KEYS.INCLUDED_PRODUCT_IDS) 
        excludedIds =  getIdsFromRuleSet(salesTargetData.ruleSet, Enum_PRODUCT_ID_KEYS.EXCLUDED_PRODUCT_IDS)
      }
    } else {
      if (!salesTargetData.tabWiseData) {
        return []
      }
      if (salesTargetData.tabWiseData?.activeTab === 'child') {
        includedIds = getIdsFromRuleSet(salesTargetData.tabWiseData?.child?.ruleSet, Enum_PRODUCT_ID_KEYS.INCLUDED_PRODUCT_IDS) 
        excludedIds = getIdsFromRuleSet(salesTargetData.tabWiseData?.child?.ruleSet, Enum_PRODUCT_ID_KEYS.EXCLUDED_PRODUCT_IDS)
      } else {
        includedIds = getIdsFromRuleSet(salesTargetData.tabWiseData?.parent?.ruleSet, Enum_PRODUCT_ID_KEYS.INCLUDED_PRODUCT_IDS)
        excludedIds = getIdsFromRuleSet(salesTargetData.tabWiseData?.parent?.ruleSet, Enum_PRODUCT_ID_KEYS.EXCLUDED_PRODUCT_IDS)
      }
    }
    return [...includedIds, ...excludedIds]
  }
  const productIds = extractProductIds()
  const {setPageIndex, setSortingObj, setSearchArry, pageIndex, sortingObj, searchArr, handleResetAll} = useDataTable()

   // Build productIdFilter if productIds are present
   const productIdFilter = useMemo(() => {
    if (productIds && productIds.length > 0) {
      return {
        field: 'productId',
        type: 'string',
        value: productIds.join('|')
      }
    }
    return null;
  }, [productIds])


  // Only apply productIdFilter if the user has explicitly filtered by productId
  // Otherwise, fetch all products and handle selection in the table UI
  const mergedSearchArr = useMemo(() => {
    if (productIdFilter && isReviewPage) {
      // Remove any previous productId filter to avoid duplicates
      const filtered = searchArr.filter(f => f.field !== 'productId')
      return [...filtered, productIdFilter]
    }

    return searchArr
  }, [searchArr, productIdFilter, isReviewPage])

  const queryParams = useMemo(() => {
    const params = new URLSearchParams()

    // Add sorting parameters if available

    if (sortingObj?.column) {
      if (sortingObj.column !== "includeExclude") {
        params.append('sortBy', sortingObj.column)
        params.append('order', sortingObj.direction)
      }
    }
    // Add pagination parameters

    let pageSizeValue = 10

    let pageValue = pageIndex || 0

    if (options?.selectAll) {
      pageSizeValue = Math.max(1000, totalRecords || 1000)
    } else if (isReviewPage && productIds?.length > 0) {
      pageSizeValue = Math.max(1, productIds.length || 1)
    }

    params.append('pageSize', pageSizeValue)

    params.append('page', pageValue)

    return params.toString()
  }, [
    sortingObj?.column,
    sortingObj?.direction,
    pageIndex,
    options?.selectAll,
    totalRecords,
    isReviewPage,
    productIds.length,
  ])

  const [{data, isLoading, isFetching}] = useQueries({
    queries: [
      {
        queryKey: ['PRODUCT_LIST', queryParams, mergedSearchArr],
        queryFn: async () => {
          const response = await getProductList(queryParams, mergedSearchArr)

          if (response?.totalRecords) {
            setTotalRecords(response.totalRecords)
          }

          return response
        },
      },
    ],
  })

  const onDownloadBtnClick = async selectedIds => {
    const body = {
      productIds: selectedIds,
      filters: mergedSearchArr,
    }

    const data = await getProductCsvFile(body, queryParams)
    const fileName = `Product_${getDateWithTimeString()}`
    downloadCSVFiles(data, fileName)
  }
  const fetchAllProducts = async (searchArr = []) => {
    try {
      const total = totalRecords

      const queryParams = `page=0&pageSize=${total}`

      const response = await getProductList(queryParams, searchArr)

      if (!response.data) {
        return []
      }

      return response.data
    } catch (error) {
      console.error('[useProductList][fetchAllProducts] Error:', error)
      throw error
    }
  }

  const productListData = useMemo(() => GetFormattedProductData(data), [data])
  const getTagProps = (included) => {
    if (included === undefined) {
      return  { text: '—', color: null }; // Default case for undefined
    }
    return {
      text: included ? 'Exclude' : 'Include',
      color: included ? TAG_COLOR.RED_150 : TAG_COLOR.GREEN_150,
    };
  };
  return {
    productListData,
    productListColConfigData: ProductListColConfig,
    setPageIndex,
    setSortingObj,
    setSearchArry,
    onDownloadBtnClick,
    pageIndex,
    isLoading: isLoading || isFetching,
    fetchAllProducts,
    totalRecords,
    handleResetAll,
    getTagProps,
    initialSortConfig:sortingObj,
    searchArr
  }
}
